import { useCallback, useEffect, useRef, useState } from "react";
import { listClientESignatures } from "@/features/esignatures/api";
import type { ESignatureRequest } from "@/features/esignatures/types";
import { ApiError } from "@/lib/api";

interface UseClientESignaturesResult {
  requests: ESignatureRequest[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetches a client's e-signature requests; race-guarded like use-client-notices. */
export function useClientESignatures(clientId: string): UseClientESignaturesResult {
  const [requests, setRequests] = useState<ESignatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    listClientESignatures(clientId)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setRequests(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setRequests([]);
        setError(err instanceof ApiError ? err.message : "Failed to load e-signatures.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, reloadKey]);

  return { requests, loading, error, refetch };
}
